import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BadgeIndianRupee,
  Building,
  MapPin,
  Ruler,
  Sparkles,
} from "lucide-react";

import { featuredProjects } from "../mock";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

const formatINR = (x) => {
  if (!x) return "";
  return x;
};

const ProjectCard = ({ p }) => {
  const images = useMemo(() => {
    const fromGallery = Array.isArray(p.galleryUrls) ? p.galleryUrls : [];
    const all = [p.imageUrl, ...fromGallery].filter(Boolean);
    // unique
    return all.filter((u, i) => all.indexOf(u) === i);
  }, [p.galleryUrls, p.imageUrl]);

  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!images.length) return;
    if (images.length === 1) return;
    const t = setInterval(() => {
      setIdx((x) => (x + 1) % images.length);
    }, 2500);
    return () => clearInterval(t);
  }, [images]);

  useEffect(() => {
    // Slightly randomize starting frame so cards don't look identical
    if (images.length > 1) {
      setIdx(Math.floor(Math.random() * images.length));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card className="group overflow-hidden border-slate-200/70 bg-white/65 shadow-sm backdrop-blur-2xl transition-transform hover:-translate-y-0.5">
      <div className="relative h-44 overflow-hidden">
        {/* Auto-fade gallery */}
        <div className="absolute inset-0">
          {images.map((url, i) => (
            <img
              key={url}
              src={url}
              alt={p.name}
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 transition-transform ${
                i === idx ? "opacity-100" : "opacity-0"
              } group-hover:scale-[1.04]`}
              style={{ transformOrigin: "center" }}
              loading={i === 0 ? "eager" : "lazy"}
            />
          ))}
        </div>

        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1220]/35 via-[#0b1220]/15 to-white/85" />

        <div className="absolute left-4 top-4 flex items-center gap-2">
          <Badge className="bg-white/75 text-slate-900 backdrop-blur-2xl hover:bg-white">
            <Sparkles className="mr-1.5 h-3.5 w-3.5 text-[#D4A72C]" />
            Featured
          </Badge>
          <Badge className="bg-[#0F766E]/12 text-[#0F766E] hover:bg-[#0F766E]/16">
            {p.builder}
          </Badge>
        </div>
      </div>

      <CardHeader className="pb-3">
        <CardTitle className="flex items-start justify-between gap-3 text-base">
          <div>
            <div className="text-slate-950">{p.name}</div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-[#0F766E]" />
                {p.location}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Building className="h-3.5 w-3.5 text-slate-500" />
                {p.type}
              </span>
            </div>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-slate-200/70 bg-white/70 p-3">
            <div className="text-xs font-medium text-slate-500">Configuration</div>
            <div className="mt-1 text-sm font-semibold text-slate-900">
              {p.config}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200/70 bg-white/70 p-3">
            <div className="text-xs font-medium text-slate-500">Starting</div>
            <div className="mt-1 text-sm font-semibold text-slate-900">
              {formatINR(p.startingPrice)}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {p.highlights.map((h) => (
            <Badge
              key={h}
              variant="secondary"
              className="bg-slate-900/5 text-slate-700"
            >
              {h}
            </Badge>
          ))}
        </div>

        {p.amenities?.length ? (
          <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-3">
            <div className="text-xs font-medium text-slate-500">Amenities</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {p.amenities.slice(0, 4).map((a) => (
                <Badge
                  key={a}
                  className="bg-[#0F766E]/10 text-[#0F766E] hover:bg-[#0F766E]/15"
                >
                  {a}
                </Badge>
              ))}
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <Ruler className="h-4 w-4 text-slate-500" />
            {p.sizeRange}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <BadgeIndianRupee className="h-4 w-4 text-[#D4A72C]" />
            {p.priceNote}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="text-xs text-slate-600">
            <span className="font-medium text-slate-700">Possession:</span> {p.possession}
          </div>
          <div className="text-xs text-slate-600">
            <span className="font-medium text-slate-700">RERA:</span> {p.rera}
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <a href="#contact" className="w-full sm:w-auto">
            <Button className="w-full bg-[#0B1220] text-white hover:bg-[#121b31]">
              Enquire for this project
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
          <div className="text-xs text-slate-500">
            Details are indicative; confirm availability.
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const FeaturedProjectsSection = () => {
  const tabs = useMemo(
    () => [
      { key: "all", label: "All" },
      { key: "M3M", label: "M3M" },
      { key: "Ashiana", label: "Ashiana" },
      { key: "Godrej", label: "Godrej" },
    ],
    []
  );

  const [active, setActive] = useState("all");

  const filtered = useMemo(() => {
    if (active === "all") return featuredProjects;
    return featuredProjects.filter((p) => p.builder === active);
  }, [active]);

  return (
    <section id="projects" className="scroll-mt-28">
      <div className="mx-auto w-full max-w-6xl px-5 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[1fr_0.9fr] lg:items-end">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/55 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur-2xl">
              <Sparkles className="h-3.5 w-3.5 text-[#0F766E]" />
              Featured projects (popular picks)
            </div>
            <h2 className="mt-4 text-pretty text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
              A few high-interest projects to explore
            </h2>
            <p className="mt-3 max-w-2xl text-pretty text-[15px] leading-7 text-slate-600 sm:text-base">
              Here are some projects people commonly ask about. If you share your budget + sector preference,
              we’ll guide you to the best match.
            </p>
          </div>

          <div className="relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white/60 p-5 shadow-sm backdrop-blur-2xl">
            {/* Subtle official-image backdrop for the CTA block */}
            <div className="pointer-events-none absolute inset-0">
              <img
                src={featuredProjects?.[0]?.imageUrl}
                alt="Featured project backdrop"
                className="h-full w-full object-cover opacity-[0.18]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/70 to-white/85" />
              <div className="absolute inset-0 [background:radial-gradient(520px_220px_at_20%_20%,rgba(15,118,110,0.16),transparent_60%),radial-gradient(520px_220px_at_85%_30%,rgba(212,167,44,0.16),transparent_60%)]" />
            </div>

            <div className="relative">
              <div className="text-sm font-semibold text-slate-950">Want options like these?</div>
              <div className="mt-1 text-sm text-slate-600">
                Get a shortlist with availability and comparable alternatives.
              </div>
              <div className="mt-4 flex gap-3">
                <a href="#contact" className="flex-1">
                  <Button className="w-full bg-[#D4A72C] text-[#0B1220] hover:bg-[#e0b63a]">
                    Request shortlist
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <a href="#builders" className="flex-1">
                  <Button
                    variant="outline"
                    className="w-full border-slate-300 bg-white/70 backdrop-blur-2xl hover:bg-white"
                  >
                    Builders
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <Tabs value={active} onValueChange={setActive} className="w-full">
            <TabsList className="w-full bg-white/70 backdrop-blur-2xl sm:w-auto">
              {tabs.map((t) => (
                <TabsTrigger key={t.key} value={t.key}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {tabs.map((t) => (
              <TabsContent key={t.key} value={t.key} className="mt-6">
                <div className="grid gap-5 lg:grid-cols-3">
                  {filtered.map((p) => (
                    <ProjectCard key={p.id} p={p} />
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </section>
  );
};
